import type { RouteLocationRaw, RouteLocationResolved, Router } from 'vue-router';
import { logger } from '@/shared/common/utils/logger.util';
import { MAX_INITIAL_ROUTE_REDIRECTS } from '../constants/mainLayout.constant';
import type {
  RouteComponentRecord,
  RouteRecordComponent,
} from '../types/mainLayout.type';

function getRedirectTarget(route: RouteLocationResolved): RouteLocationRaw | null {
  const redirect = route.matched[route.matched.length - 1]?.redirect;
  if (!redirect) return null;

  const target = typeof redirect === 'function' ? redirect(route) : redirect;

  if (typeof target === 'string') {
    const hasOwnQueryOrHash = target.includes('?') || target.includes('#');
    return hasOwnQueryOrHash
      ? target
      : { path: target, query: route.query, hash: route.hash };
  }

  const inheritedParams = 'path' in target ? {} : { params: route.params };
  return { query: route.query, hash: route.hash, ...inheritedParams, ...target };
}

/**
 * `router.resolve` no aplica los `redirect` (los aplica `push`), así que en una entrada
 * en frío a `/` el nombre y la meta serían los del registro que redirige. Sigue los
 * `redirect` del registro más profundo, como hace el router, con un tope contra ciclos.
 * Si un destino no resuelve, devuelve la última ubicación válida y lo loguea.
 */
export function resolveInitialLocation(
  router: Pick<Router, 'resolve'>,
  location: string
): RouteLocationResolved {
  let route = router.resolve(location);

  try {
    for (let hop = 0; hop < MAX_INITIAL_ROUTE_REDIRECTS; hop += 1) {
      const target = getRedirectTarget(route);
      if (!target) return route;

      route = router.resolve(target);
    }

    logger.warn('[resolveInitialLocation] Demasiados redirects encadenados', {
      location,
    });
  } catch (error) {
    logger.warn('[resolveInitialLocation] No se pudo resolver un redirect', {
      location,
      error,
    });
  }

  return route;
}

/** Mismo criterio que vue-router para distinguir un componente de un loader diferido. */
function isLazyRouteComponent(
  component: RouteRecordComponent
): component is () => Promise<unknown> {
  return (
    typeof component === 'function' &&
    !('displayName' in component) &&
    !('props' in component) &&
    !('__vccOpts' in component)
  );
}

function getDefaultExport(loaded: unknown): unknown {
  const isModule = typeof loaded === 'object' && loaded !== null && 'default' in loaded;
  return isModule ? loaded.default : loaded;
}

function loadRouteComponent(component: RouteRecordComponent): Promise<unknown> {
  if (!isLazyRouteComponent(component)) return Promise.resolve(component);

  return component().then(getDefaultExport);
}

/**
 * Cada archivo de rutas declara su propio loader de MainLayout, así que no se puede
 * comparar la función: se cargan y se compara el componente (un módulo es único). Carga
 * en paralelo y responde `true` apenas un registro coincide; un chunk que falla cuenta
 * como no coincidente y se loguea.
 */
export function routeUsesLayout(
  records: RouteComponentRecord[],
  loadLayout: () => Promise<unknown>
): Promise<boolean> {
  const components = records.flatMap((record) =>
    record.components?.default ? [record.components.default] : []
  );
  if (!components.length) return Promise.resolve(false);

  const layoutPromise = loadLayout().then(getDefaultExport);
  const checks = components.map((component) =>
    Promise.all([layoutPromise, loadRouteComponent(component)])
      .then(([layout, loaded]) => layout === loaded)
      .catch((error: unknown) => {
        logger.warn('[routeUsesLayout] No se pudo cargar un componente de la ruta', {
          error,
        });
        return false;
      })
  );

  return new Promise((resolve) => {
    let pendingChecks = checks.length;

    checks.forEach((check) =>
      check.then((isLayout) => {
        if (isLayout) resolve(true);

        pendingChecks -= 1;
        if (pendingChecks === 0) resolve(false);
      })
    );
  });
}
